import React from 'react';
import { Field } from 'formik';
import { tv } from 'tailwind-variants';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import RemixIcon from 'helpers/RemixIcon/RemixIcon';

const formFieldStyles = tv({
  base: [
    'w-full',
    'min-h-12',
    'px-spacing-spacing-16',
    'py-spacing-spacing-12',
    'border',
    'border-color-input-border',
    'rounded-[8px]',
    'bg-color-grayscale-white',
    'placeholder:text-color-text-text-secondary',
    'text-color-text-text',
    'hover:border-color-input-border-hover',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'leading-typography-line-height-body-medium',
    'placeholder:italic',
    'font-normal',
    'disabled:text-color-text-text-disabled',
    'disabled:cursor-not-allowed',
    'focus:outline-none',
    'focus:ring-1',
    'focus:border-color-input-border-focused',
    'focus:ring-color-input-border-focused',
    'focus:ring-shadow-[0_0_0_1px_var(--color-core-color-4-SkyBlue)]',
  ],
  variants: {
    state: {
      default: '',
      error: [
        'border-color-text-text-critical',
        'focus:border-color-text-text-error',
        'bg-color-fill-error-light',
      ],
      disabled: ['bg-color-grayscale-gray-100', 'cursor-not-allowed'],
    },
    iconPadding: {
      none: '',
      prefix: 'pl-12',
      suffix: 'pr-12',
      both: 'pl-12 pr-12',
    },
    prefixTextPadding: {
      true: 'pl-6',
      false: '',
    },
  },
  defaultVariants: {
    state: 'default',
    iconPadding: 'none',
  },
});

interface TextFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
  isTouched?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  errors?: Record<string, string | undefined>;
  touched?: Record<string, boolean | undefined>;
  hideLabel?: boolean;
  isStandalone?: boolean;

  /** Use your DS icon names */
  prefixText?: string;
  prefixIcon?: string;
  suffixIcon?: string;
  prefixIconAriaLabel?: string;
  suffixIconAriaLabel?: string;
}

export const TextField: React.FC<TextFieldProps> = ({
  field,
  hasError = false,
  value,
  onChange,
  onBlur,
  errors,
  touched,
  hideLabel,
  isStandalone = false,
  prefixText,
  prefixIcon,
  suffixIcon,
  prefixIconAriaLabel,
  suffixIconAriaLabel,
}) => {
  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const disabled = fieldConfig?.disabled?.value;
  const required = fieldConfig?.required?.value;
  const characterLimit = fieldConfig?.characterLimit?.value;
  const placeholder = fieldConfig?.placeholderText?.value;

  if (!fieldName) return null;

  const fieldState = disabled ? 'disabled' : hasError ? 'error' : 'default';

  const hasPrefix = Boolean(prefixIcon);
  const hasSuffix = Boolean(suffixIcon);
  const hasPrefixText = Boolean(prefixText);
  const iconPadding =
    hasPrefix && hasSuffix ? 'both' : hasPrefix ? 'prefix' : hasSuffix ? 'suffix' : 'none';
  const prefixTextPadding = hasPrefixText ? true : false;

  const commonFieldProps = {
    name: fieldName,
    id: fieldName,
    disabled,
    className: formFieldStyles({ state: fieldState, iconPadding, prefixTextPadding }),
    'data-automation': `textField-${fieldName}`,
  };

  const IconWrap = ({
    side,
    ariaLabel,
    icon,
  }: {
    side: 'left' | 'right';
    ariaLabel?: string;
    icon: string;
  }) => (
    <span
      className={[
        'absolute pointer-events-none top-1/2 -translate-y-1/2',
        'text-color-grayscale-gray-black',
        side === 'left' ? 'left-4' : 'right-4',
      ].join(' ')}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
    >
      <RemixIcon name={icon} size="sm" />
    </span>
  );

  return (
    <FormFieldWrapper
      key={fieldName}
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isDisabled={disabled}
      hasError={hasError}
      isRequired={required}
      errors={errors}
      touched={touched}
      hideLabel={hideLabel}
    >
      {isStandalone ? (
        <div className="relative">
          {prefixIcon && <IconWrap side="left" ariaLabel={prefixIconAriaLabel} icon={prefixIcon} />}
          {prefixText && (
            <span className="text-color-text-text-secondary font-typography-font-family-body text-typography-font-size-body-medium leading-typography-line-height-body-medium text-p-color-text-secondary absolute top-1/2 -translate-y-1/2 left-4">
              {prefixText}
            </span>
          )}
          <input
            type="text"
            maxLength={characterLimit}
            placeholder={placeholder}
            aria-required={required}
            value={value ?? ''}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            {...commonFieldProps}
          />

          {suffixIcon && (
            <IconWrap side="right" ariaLabel={suffixIconAriaLabel} icon={suffixIcon} />
          )}
        </div>
      ) : (
        <div className="relative">
          {prefixIcon && <IconWrap side="left" ariaLabel={prefixIconAriaLabel} icon={prefixIcon} />}

          <Field
            type="text"
            maxLength={characterLimit}
            placeholder={placeholder}
            aria-required={required}
            {...commonFieldProps}
          />

          {suffixIcon && (
            <IconWrap side="right" ariaLabel={suffixIconAriaLabel} icon={suffixIcon} />
          )}
        </div>
      )}
    </FormFieldWrapper>
  );
};

export default TextField;
